import React from 'react';
import { Card } from "react-bootstrap";
import styles from '../SessionHistoryDetailView.module.css';

const ClientPresentationSection = ({
  sessionFormData,
  updateFormData,
  focusOfSessionError,
  symptomsError,
  moodAffectError,
  behaviorEngagementError,
  handleSymptomChange,
  autoResizeTextarea,
  setFocusOfSessionError,
  setMoodAffectError,
  setBehaviorEngagementError
}) => {
  const symptomOptions = [
    "Anxiety",
    "Depressed mood",
    "Irritability",
    "Stress",
    "Appetite changes",
    "Fatigue / low energy",
    "Sleep disturbance",
    "Other",
    "Memory difficulties",
    "Poor concentration / focus",
    "Racing thoughts",
    "Panic symptoms",
    "Dissociation / spacing out",
    "Change in mood",
    "Suicidal ideation",
    "Homicidal ideation"
  ];

  const moodAffectOptions = [
    "Stable",
    "Anxious",
    "Depressed",
    "Irritable",
    "Euthymic (normal)",
    "Labile (fluctuating)",
    "Tearful",
    "Flat",
    "Other"
  ];

  const behaviorEngagementOptions = [
    "Cooperative",
    "Engaged",
    "Guarded",
    "Distracted",
    "Withdrawn",
    "Resistant",
    "Restless",
    "Calm",
    "Other"
  ];

  const leftColumnSymptoms = symptomOptions.slice(0, Math.ceil(symptomOptions.length / 2));
  const rightColumnSymptoms = symptomOptions.slice(Math.ceil(symptomOptions.length / 2));

  return (
    <Card id="client-presentation" className={styles.card}>
      <h3 className={styles.sectionHeading}>
        Client Presentation
      </h3>

      {/* Focus of the Session */}
      <div style={{ marginBottom: "24px" }}>
        <label className={styles.labelWithMargin}>
          Focus of the session.
        </label>
        <textarea
          value={sessionFormData.focusOfSession || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 500) {
              updateFormData("focusOfSession", value);
              if (focusOfSessionError && value.trim()) {
                setFocusOfSessionError("");
              }
            }
          }}
          placeholder="Free-text"
          className={styles.textarea}
          maxLength={500}
          required
        />
        {focusOfSessionError && (
          <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
            {focusOfSessionError}
          </div>
        )}
      </div>

      <div className={styles.separator}></div>

      {/* Symptoms - Multi-select checkboxes */}
      <div style={{ marginBottom: "24px" }}>
        <label className={styles.labelWithMarginLarge}>
          Symptoms
        </label>
        {symptomsError && (
          <div className={styles.errorMessage} style={{ marginTop: "8px", marginBottom: "12px" }}>
            {symptomsError}
          </div>
        )}
        <div className={styles.checkboxRow}>
          <div className={styles.checkboxColumn}>
            {leftColumnSymptoms.map((item) => (
              <div key={item} className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id={`symptom-${item}`}
                  checked={sessionFormData.selectedSymptoms?.includes(item) || false}
                  onChange={() => handleSymptomChange(item)}
                  className={styles.checkbox}
                />
                <label htmlFor={`symptom-${item}`} className={styles.checkboxLabel}>
                  {item}
                </label>
              </div>
            ))}
          </div>
          <div className={styles.checkboxColumn}>
            {rightColumnSymptoms.map((item) => (
              <div key={item} className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id={`symptom-${item}`}
                  checked={sessionFormData.selectedSymptoms?.includes(item) || false}
                  onChange={() => handleSymptomChange(item)}
                  className={styles.checkbox}
                />
                <label htmlFor={`symptom-${item}`} className={styles.checkboxLabel}>
                  {item}
                </label>
              </div>
            ))}
          </div>
        </div>
        {sessionFormData.selectedSymptoms?.includes("Other") && (
          <div style={{ marginTop: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginBottom: "4px" }}>
              <div style={{ fontSize: "12px", color: "#8E8E93", whiteSpace: "nowrap" }}>
                {sessionFormData.otherSymptomText?.length || 0}/100
              </div>
            </div>
            <textarea
              id="symptom-other-textarea"
              value={sessionFormData.otherSymptomText || ""}
              onChange={(e) => {
                autoResizeTextarea(e.target);
                const value = e.target.value;
                if (value.length <= 100) {
                  updateFormData("otherSymptomText", value);
                }
              }}
              placeholder="Please specify."
              className={styles.otherTextInput}
              rows={1}
              style={{ minHeight: "34px", resize: "none", overflow: "hidden" }}
              maxLength={100}
            />
          </div>
        )}
      </div>

      <div className={styles.separator}></div>

      {/* Mood / Affect Dropdown */}
      <div style={{ marginBottom: "24px" }}>
        <label className={styles.labelWithMarginXLarge}>
          Mood / Affect
        </label>
        <select
          value={sessionFormData.moodAffect || ""}
          onChange={(e) => {
            updateFormData("moodAffect", e.target.value);
            if (moodAffectError && e.target.value) {
              setMoodAffectError("");
            }
            if (e.target.value !== "Other") {
              updateFormData("otherMoodAffectText", "");
            }
          }}
          className={styles.selectMediumWidth}
          required
        >
          <option value="">-</option>
          {moodAffectOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {sessionFormData.moodAffect === "Other" && (
          <div style={{ marginTop: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginBottom: "4px" }}>
              <div style={{ fontSize: "12px", color: "#8E8E93", whiteSpace: "nowrap" }}>
                {sessionFormData.otherMoodAffectText?.length || 0}/100
              </div>
            </div>
            <textarea
              id="mood-affect-other-textarea"
              value={sessionFormData.otherMoodAffectText || ""}
              onChange={(e) => {
                autoResizeTextarea(e.target);
                const value = e.target.value;
                if (value.length <= 100) {
                  updateFormData("otherMoodAffectText", value);
                }
              }}
              placeholder="Please specify."
              className={styles.otherTextInput}
              rows={1}
              style={{ minHeight: "34px", resize: "none", overflow: "hidden" }}
              maxLength={100}
            />
          </div>
        )}
        {moodAffectError && (
          <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
            {moodAffectError}
          </div>
        )}
      </div>

      <div className={styles.separator}></div>

      {/* Behavior / Engagement Dropdown */}
      <div style={{ marginBottom: "16px" }}>
        <label className={styles.labelWithMarginXLarge}>
          Behavior / Engagement
        </label>
        <select
          value={sessionFormData.behaviorEngagement || ""}
          onChange={(e) => {
            updateFormData("behaviorEngagement", e.target.value);
            if (behaviorEngagementError && e.target.value) {
              setBehaviorEngagementError("");
            }
            if (e.target.value !== "Other") {
              updateFormData("otherBehaviorEngagementText", "");
            }
          }}
          className={styles.selectMediumWidth}
          required
        >
          <option value="">-</option>
          {behaviorEngagementOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {sessionFormData.behaviorEngagement === "Other" && (
          <div style={{ marginTop: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginBottom: "4px" }}>
              <div style={{ fontSize: "12px", color: "#8E8E93", whiteSpace: "nowrap" }}>
                {sessionFormData.otherBehaviorEngagementText?.length || 0}/100
              </div>
            </div>
            <textarea
              id="behavior-engagement-other-textarea"
              value={sessionFormData.otherBehaviorEngagementText || ""}
              onChange={(e) => {
                autoResizeTextarea(e.target);
                const value = e.target.value;
                if (value.length <= 100) {
                  updateFormData("otherBehaviorEngagementText", value);
                }
              }}
              placeholder="Please specify."
              className={styles.otherTextInput}
              rows={1}
              style={{ minHeight: "34px", resize: "none", overflow: "hidden" }}
              maxLength={100}
            />
          </div>
        )}
        {behaviorEngagementError && (
          <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
            {behaviorEngagementError}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ClientPresentationSection;

